import { AuthenticatedUser } from 'modules/auth/domain/interfaces/auth-sign-in-response';
import { Organizer } from 'modules/organizer/domain/interfaces/organizer';

/**
 * User properties пользователя в Amplitude.
 *
 * IMPORTANT: только числовые id и справочные значения — никаких ФИО, телефонов и e-mail.
 * Расшифровка «кто это» делается в админке по `user_id`. Набор ключей проверяется тестом
 * точным сравнением: случайно добавленное PII-свойство роняет сьюту.
 */
export interface AnalyticsUserProperties {
  /** Организация-тенант (backend `tournamentId`), а не соревнование. */
  organizer_id?: number;
  organizer_sport_type?: string;
  roles: string[];
}

export interface AnalyticsIdentity {
  /** `setUserId` принимает строку; id уезжает как есть, без префикса (см. `minIdLength` в `amplitude.ts`). */
  userId: string;
  /** Группа `organizer` в Amplitude. `null` — организация ещё неизвестна, группу не ставим. */
  organizerId: number | null;
  userProperties: AnalyticsUserProperties;
}

/**
 * Собирает идентичность для Amplitude из того, что есть в сторе. Чистая функция: вызовы SDK
 * живут в `use-analytics-identity.ts`.
 *
 * `activeTournamentId` — второй источник id организации помимо загруженной карточки: он
 * приезжает вместе с авторизацией, тогда как `organizer.info` появляется только после
 * ответа `fetchOrganizer`. Без него первые события после входа уходили бы без `organizer_id`.
 */
export function buildAnalyticsIdentity(
  user: AuthenticatedUser | null,
  organizer: Organizer | null,
  activeTournamentId: number | null,
): AnalyticsIdentity | null {
  if (!user) {
    return null;
  }

  const organizerId = organizer?.id ?? activeTournamentId ?? null;

  const userProperties: AnalyticsUserProperties = { roles: user.roles ?? [] };

  // Ключи с `undefined` не добавляем вовсе: Amplitude сохранил бы такое свойство пустым,
  // и оно навсегда осталось бы в списке свойств пользователя.
  if (organizerId !== null) {
    userProperties.organizer_id = organizerId;
  }
  if (organizer?.sportType) {
    userProperties.organizer_sport_type = organizer.sportType;
  }

  return { userId: String(user.id), organizerId, userProperties };
}
